import {
  type Backend,
  type CollectionId,
  type CollectionNotFound,
  type ConversationId,
  type Document,
  type DocumentContentPatchNotValid,
  type DocumentContentNotValid,
  type DocumentId,
  type DocumentNotFound,
  DocumentVersionCreator,
  type DocumentVersionId,
  type DocumentContentChange,
  DocumentContentChangeType,
  type DocumentVersionIdNotMatching,
  type FilesNotFound,
  type MakingContentBlockingKeysFailed,
  type ReferencedDocumentsNotFound,
  type UnexpectedError,
} from "@superego/backend";
import type { ResultPromise } from "@superego/global-types";
import { type DocumentRef, utils, valibotSchemas } from "@superego/schema";
import {
  Id,
  makeSuccessfulResult,
  makeUnsuccessfulResult,
} from "@superego/shared-utils";
import * as v from "valibot";
import type DocumentVersionEntity from "../../entities/DocumentVersionEntity.js";
import type FileEntity from "../../entities/FileEntity.js";
import makeContentBlockingKeys from "../../makers/makeContentBlockingKeys.js";
import makeContentSummary from "../../makers/makeContentSummary.js";
import makeDocument from "../../makers/makeDocument.js";
import makeResultError from "../../makers/makeResultError.js";
import makeValidationIssues from "../../makers/makeValidationIssues.js";
import * as structuralSchemas from "../../structural-schemas/index.js";
import applyDocumentContentPatch from "../../utils/applyDocumentContentPatch.js";
import assertCollectionVersionExists from "../../utils/assertCollectionVersionExists.js";
import assertDocumentVersionExists from "../../utils/assertDocumentVersionExists.js";
import BackendUsecase from "../../utils/BackendUsecase.js";
import ContentDocumentRefUtils from "../../utils/ContentDocumentRefUtils.js";
import ContentFileUtils from "../../utils/ContentFileUtils.js";
import difference from "../../utils/difference.js";
import isEmpty from "../../utils/isEmpty.js";

type ExecReturnValue = ResultPromise<
  Document,
  | CollectionNotFound
  | DocumentNotFound
  | DocumentVersionIdNotMatching
  | DocumentContentPatchNotValid
  | DocumentContentNotValid
  | MakingContentBlockingKeysFailed
  | FilesNotFound
  | ReferencedDocumentsNotFound
  | UnexpectedError
>;
export default class DocumentsCreateNewVersion extends BackendUsecase<
  Backend["documents"]["createNewVersion"]
> {
  argumentsSchema = v.tuple([
    structuralSchemas.backend.ids.collectionId(),
    structuralSchemas.backend.ids.documentId(),
    structuralSchemas.backend.ids.documentVersionId(),
    structuralSchemas.backend.types.documentContentChange(),
  ]);
  resultSchema = structuralSchemas.global.result(
    structuralSchemas.backend.types.document(),
    [
      structuralSchemas.backend.errors.collectionNotFound(),
      structuralSchemas.backend.errors.documentContentNotValid(),
      structuralSchemas.backend.errors.documentContentPatchNotValid(),
      structuralSchemas.backend.errors.documentNotFound(),
      structuralSchemas.backend.errors.documentVersionIdNotMatching(),
      structuralSchemas.backend.errors.filesNotFound(),
      structuralSchemas.backend.errors.makingContentBlockingKeysFailed(),
      structuralSchemas.backend.errors.referencedDocumentsNotFound(),
      structuralSchemas.backend.errors.unexpectedError(),
    ],
  );

  async exec(
    collectionId: CollectionId,
    id: DocumentId,
    latestVersionId: DocumentVersionId,
    contentChange: DocumentContentChange,
  ): ExecReturnValue;
  async exec(
    collectionId: CollectionId,
    id: DocumentId,
    latestVersionId: DocumentVersionId,
    contentChange: DocumentContentChange,
    options:
      | {
          createdBy: DocumentVersionCreator.Assistant;
          conversationId: ConversationId;
        }
      | {
          createdBy: DocumentVersionCreator.Migration;
        },
  ): ExecReturnValue;
  async exec(
    collectionId: CollectionId,
    id: DocumentId,
    latestVersionId: DocumentVersionId,
    contentChange: DocumentContentChange,
    options?: {
      createdBy:
        | DocumentVersionCreator.Assistant
        | DocumentVersionCreator.Migration;
      conversationId?: ConversationId;
    },
  ): ExecReturnValue {
    const collection = await this.repos.collection.find(collectionId);
    if (!collection) {
      return makeUnsuccessfulResult(
        makeResultError("CollectionNotFound", { collectionId }),
      );
    }

    const document = await this.repos.document.find(id);
    if (!document || document.collectionId !== collectionId) {
      return makeUnsuccessfulResult(
        makeResultError("DocumentNotFound", { documentId: id }),
      );
    }

    const latestVersion =
      await this.repos.documentVersion.findLatestWhereDocumentIdEq(id);
    assertDocumentVersionExists(document.collectionId, id, latestVersion);

    if (latestVersionId !== latestVersion.id) {
      return makeUnsuccessfulResult(
        makeResultError("DocumentVersionIdNotMatching", {
          documentId: id,
          latestVersionId: latestVersion.id,
          suppliedVersionId: latestVersionId,
        }),
      );
    }

    const latestCollectionVersion =
      await this.repos.collectionVersion.findLatestWhereCollectionIdEq(
        document.collectionId,
      );
    assertCollectionVersionExists(
      document.collectionId,
      latestCollectionVersion,
    );

    let contentToValidate: unknown;
    if (contentChange.type === DocumentContentChangeType.Full) {
      contentToValidate = contentChange.content;
    } else {
      const applyPatchResult = applyDocumentContentPatch(
        collectionId,
        id,
        latestVersionId,
        latestVersion.content,
        contentChange.patch,
      );
      if (!applyPatchResult.success) {
        return applyPatchResult;
      }
      contentToValidate = applyPatchResult.data;
    }

    const contentValidationResult = v.safeParse(
      valibotSchemas.content(latestCollectionVersion.schema),
      contentToValidate,
    );
    if (!contentValidationResult.success) {
      return makeUnsuccessfulResult(
        makeResultError("DocumentContentNotValid", {
          collectionId: document.collectionId,
          collectionVersionId: latestCollectionVersion.id,
          documentId: id,
          issues: makeValidationIssues(contentValidationResult.issues),
        }),
      );
    }

    const referencedDocuments = ContentDocumentRefUtils.extractDocumentRefs(
      latestCollectionVersion.schema,
      contentValidationResult.output,
    );
    const notFoundDocumentRefs: DocumentRef[] = [];
    for (const referencedDocument of referencedDocuments) {
      const exists = await this.repos.document.exists(
        referencedDocument.documentId as DocumentId,
      );
      if (!exists) {
        notFoundDocumentRefs.push(referencedDocument);
      }
    }
    if (!isEmpty(notFoundDocumentRefs)) {
      return makeUnsuccessfulResult(
        makeResultError("ReferencedDocumentsNotFound", {
          collectionId: document.collectionId,
          documentId: id,
          notFoundDocumentRefs,
        }),
      );
    }

    const referencedFileIds = ContentFileUtils.extractReferencedFileIds(
      latestCollectionVersion.schema,
      contentValidationResult.output,
    );
    const referencedFiles =
      await this.repos.file.findAllWhereIdIn(referencedFileIds);
    const missingFileIds = difference(
      referencedFileIds,
      referencedFiles.map(({ id: fileId }) => fileId),
    );
    if (!isEmpty(missingFileIds)) {
      return makeUnsuccessfulResult(
        makeResultError("FilesNotFound", { fileIds: missingFileIds }),
      );
    }

    let contentBlockingKeys: string[] | null = null;
    if (latestCollectionVersion.settings.contentBlockingKeysGetter !== null) {
      const makeContentBlockingKeysResult = await makeContentBlockingKeys(
        this.javascriptSandbox,
        latestCollectionVersion,
        id,
        contentValidationResult.output,
      );
      if (!makeContentBlockingKeysResult.success) {
        return makeContentBlockingKeysResult;
      }
      contentBlockingKeys = makeContentBlockingKeysResult.data;
    }

    const now = new Date();
    const { convertedContent, protoFilesWithIds } =
      ContentFileUtils.extractAndConvertProtoFiles(
        latestCollectionVersion.schema,
        contentValidationResult.output,
      );
    const documentVersionId = Id.generate.documentVersion();
    const contentSummary = await makeContentSummary(
      this.javascriptSandbox,
      latestCollectionVersion,
      { id: documentVersionId, documentId: id, content: convertedContent },
    );
    const documentVersion: DocumentVersionEntity = {
      id: documentVersionId,
      previousVersionId: latestVersionId,
      documentId: id,
      collectionId: document.collectionId,
      collectionVersionId: latestCollectionVersion.id,
      conversationId: options?.conversationId ?? null,
      content: convertedContent,
      contentBlockingKeys: contentBlockingKeys,
      referencedDocuments: referencedDocuments,
      contentSummary: contentSummary,
      createdBy: options?.createdBy ?? DocumentVersionCreator.User,
      createdAt: now,
    };
    const textChunks = utils.extractTextChunks(
      latestCollectionVersion.schema,
      convertedContent,
    );
    const filesWithContent: (FileEntity & {
      content: Uint8Array<ArrayBuffer>;
    })[] = protoFilesWithIds.map((protoFileWithId) => ({
      id: protoFileWithId.id,
      referencedBy: [
        {
          collectionId: collectionId,
          documentId: id,
          documentVersionId: documentVersion.id,
        },
      ],
      createdAt: now,
      content: protoFileWithId.content,
    }));
    await this.repos.documentVersion.insert(documentVersion);
    await this.repos.documentTextSearchIndex.upsert(
      collectionId,
      id,
      textChunks,
    );
    await this.repos.file.addReferenceToAll(referencedFileIds, {
      collectionId: collectionId,
      documentId: id,
      documentVersionId: documentVersion.id,
    });
    await this.repos.file.insertAll(filesWithContent);

    return makeSuccessfulResult(makeDocument(document, documentVersion));
  }
}
